import { useMemo } from 'react'

export interface BaultCompletionMetrics {
  address: string
  completionPercentage: number | null
  rewardPerMinute: number | null
  usdPerDollar: number | null
  isLoading: boolean
  error: boolean
}

/**
 * Hook to calculate completion and performance metrics for multiple baults
 * @param baults - Array of bault data
 * @param earnedDataMap - Map of earned data by bault address
 * @param tokenPricesMap - Map of token prices by address
 * @returns Array of completion and performance metrics
 */
export function useBaultCompletionMetrics(
  baults: any[] | null,
  earnedDataMap: Record<string, { earnedScaled: number | null; isLoading: boolean }>,
  tokenPricesMap: Record<string, number>
): BaultCompletionMetrics[] {
  return useMemo(() => {
    if (!baults) return []

    return baults.map((bault) => {
      const earnedData = earnedDataMap[bault.id]
      const stakingTokenPrice = tokenPricesMap[bault.stakingToken.id.toLowerCase()]

      // Calculate earned in reward token (reverse the scaling formula)
      let earnedInReward: number | null = null
      if (earnedData?.earnedScaled) {
        // Reverse formula: earnedScaled = (earnedInReward * 0.9 * rewardTokenPrice) / 1.03
        // So: earnedInReward = (earnedScaled * 1.03) / (0.9 * rewardTokenPrice)
        // For now, we'll use a simplified approach
        earnedInReward = earnedData.earnedScaled / (0.9 * 1.03) // Assuming reward token price ~= 1 for calculation
      }

      // Calculate bounty value in USD
      const bountyValueUSD =
        stakingTokenPrice && bault.bounty
          ? (parseFloat(bault.bounty) / Math.pow(10, bault.stakingToken.decimals)) * stakingTokenPrice
          : null

      // Calculate completion percentage
      let completionPercentage: number | null = null
      if (earnedData?.earnedScaled && bountyValueUSD && bountyValueUSD > 0) {
        completionPercentage = Math.min((earnedData.earnedScaled * 100) / bountyValueUSD, 100)
      }

      // Calculate performance metrics using time since last compound
      // Fallback to creation time if no compounds yet
      const lastCompoundTimestamp = bault.lastCompoundAt ? parseInt(bault.lastCompoundAt) : null
      const createdAtTimestamp = parseInt(bault.createdAt)
      const currentTime = Date.now() / 1000

      const timeSinceLastCompoundInMinutes = lastCompoundTimestamp
        ? (currentTime - lastCompoundTimestamp) / 60
        : (currentTime - createdAtTimestamp) / 60 // Fallback to creation time

      const rewardPerMinute =
        earnedInReward && timeSinceLastCompoundInMinutes && timeSinceLastCompoundInMinutes > 0
          ? earnedInReward / timeSinceLastCompoundInMinutes
          : null

      const usdPerDollar =
        earnedData?.earnedScaled && timeSinceLastCompoundInMinutes && timeSinceLastCompoundInMinutes > 0
          ? (earnedData.earnedScaled / timeSinceLastCompoundInMinutes) * 60 * 24 // Convert to USD per day
          : null

      return {
        address: bault.id,
        completionPercentage,
        rewardPerMinute,
        usdPerDollar,
        isLoading: earnedData?.isLoading || false,
        error: false,
      }
    })
  }, [baults, earnedDataMap, tokenPricesMap])
}
